import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { getApiErrorField, withErrorHandling } from '@/client/utils/errors';
import {
  AppVersionConfig,
  AppVersionEnvelope,
  AppVersionFieldErrors,
  AppVersionUpdatePayload,
  MaintenanceConfig,
  MaintenanceTogglePayload,
} from '@/client/types/app-version';
import { appVersionKeys } from './app-version.query';

/** The `<section>.<field>` map from a 422, or {} for any other failure. */
export function getAppVersionFieldErrors(error: unknown): AppVersionFieldErrors {
  const errors = getApiErrorField<AppVersionFieldErrors>(error, 'errors');
  return errors && typeof errors === 'object' ? errors : {};
}

const useUpdateAppVersionFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (payload: AppVersionUpdatePayload): Promise<AppVersionConfig> => {
      const res = await request<AppVersionEnvelope<AppVersionConfig>>(
        '/api/admin/app-version',
        'PUT',
        payload,
      );
      return res.data;
    },
    'Failed to save the app version config',
  );
};

export const useUpdateAppVersionMutation = () => {
  const queryClient = useQueryClient();
  const updateFn = useUpdateAppVersionFn();
  return useMutation({
    mutationFn: (payload: AppVersionUpdatePayload) => updateFn(payload),
    onSuccess: (saved) => {
      queryClient.setQueryData(appVersionKeys.config(), saved);
    },
  });
};

const useToggleMaintenanceFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(
    async (payload: MaintenanceTogglePayload): Promise<MaintenanceConfig> => {
      const res = await request<AppVersionEnvelope<MaintenanceConfig>>(
        '/api/admin/app-version/maintenance',
        'POST',
        payload,
      );
      return res.data;
    },
    'Failed to update maintenance mode',
  );
};

export const useToggleMaintenanceMutation = () => {
  const queryClient = useQueryClient();
  const toggleFn = useToggleMaintenanceFn();
  return useMutation({
    mutationFn: (payload: MaintenanceTogglePayload) => toggleFn(payload),
    onSuccess: (maintenance) => {
      queryClient.setQueryData<AppVersionConfig>(appVersionKeys.config(), (prev) =>
        prev ? { ...prev, maintenance } : prev,
      );
      // The response omits updated_at, so refetch to pick up the new timestamp.
      queryClient.invalidateQueries({ queryKey: appVersionKeys.config() });
    },
  });
};
